import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';

const EMPTY_SET = new Set<string>();
const HIGHLIGHT_CLASS = 'field-highlight';
const HIGHLIGHT_DURATION_MS = 2000;

const FieldHighlightContext = createContext<
  | {
      highlightedFieldPaths: Set<string>;
      highlightField: (fieldPath: string) => void;
      highlightFields: (fieldPaths: string[]) => void;
      clearHighlight: () => void;
    }
  | undefined
>(undefined);

function applyHighlightClass(fieldPaths: string[]): HTMLElement[] {
  const elements: HTMLElement[] = [];
  for (const fp of fieldPaths) {
    const el = document.querySelector(`[data-fieldpath="${CSS.escape(fp)}"]`) as HTMLElement | null;
    if (el) {
      el.classList.add(HIGHLIGHT_CLASS);
      elements.push(el);
    }
  }
  return elements;
}

export function FieldHighlightProvider({ children }: { children: React.ReactNode }) {
  const [highlightedFieldPaths, setHighlightedFieldPaths] = useState<Set<string>>(EMPTY_SET);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const highlightedElementsRef = useRef<HTMLElement[]>([]);

  const resetDOMHighlights = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    for (const el of highlightedElementsRef.current) {
      el.classList.remove(HIGHLIGHT_CLASS);
    }
    highlightedElementsRef.current = [];
  }, []);

  const applyHighlights = useCallback(
    (fieldPaths: string[], scrollToFirst: boolean) => {
      resetDOMHighlights();

      setHighlightedFieldPaths(new Set(fieldPaths));

      const elements = applyHighlightClass(fieldPaths);
      highlightedElementsRef.current = elements;

      if (scrollToFirst && elements[0]) {
        elements[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
        elements[0].focus();
      }

      timeoutRef.current = setTimeout(() => {
        resetDOMHighlights();
        setHighlightedFieldPaths(EMPTY_SET);
      }, HIGHLIGHT_DURATION_MS);
    },
    [resetDOMHighlights]
  );

  const highlightField = useCallback(
    (fieldPath: string) => applyHighlights([fieldPath], true),
    [applyHighlights]
  );

  const highlightFields = useCallback(
    (fieldPaths: string[]) => applyHighlights(fieldPaths, true),
    [applyHighlights]
  );

  const clearHighlight = useCallback(() => {
    resetDOMHighlights();
    setHighlightedFieldPaths(EMPTY_SET);
  }, [resetDOMHighlights]);

  useEffect(() => resetDOMHighlights, [resetDOMHighlights]);

  const value = useMemo(
    () => ({ highlightedFieldPaths, highlightField, highlightFields, clearHighlight }),
    [highlightedFieldPaths, highlightField, highlightFields, clearHighlight]
  );

  return <FieldHighlightContext.Provider value={value}>{children}</FieldHighlightContext.Provider>;
}

export const useFieldHighlight = () => {
  const context = useContext(FieldHighlightContext);
  if (!context) {
    return {
      highlightedFieldPaths: EMPTY_SET,
      highlightField: () => {},
      highlightFields: () => {},
      clearHighlight: () => {},
    };
  }
  return context;
};
